import { useEffect, useRef, useState } from 'react';
import {
  listScenarios,
  createScenario,
  updateScenario,
  deleteScenario,
} from '../api/client';
import type {
  TestScenario,
  ScenarioSide,
  ExpectedMatch,
  ExpectedTerminal,
  ExpectedDot,
} from '../api/types';
import ScenarioRuns from '../components/scenarios/ScenarioRuns';

const STORY_TYPES = ['stranger', 'crush', 'ex'] as const;
const GENDERS = ['female', 'male', ''] as const;

function typeBadgeClass(type: string) {
  switch (type) {
    case 'crush':
      return 'bg-pink-100 text-pink-700';
    case 'ex':
      return 'bg-purple-100 text-purple-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

/** The permanent-dots oracle, read-only. It's authored in the seed files
 *  (`test-scenarios/*.json` → `seed-scenarios.mjs`), not here — the editor
 *  carries it through untouched so saving a scenario never drops it.
 *  null = not under test; [] = asserts the run writes no dots at all. */
function ExpectedDotsPanel({ dots }: { dots: ExpectedDot[] | null }) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-sm font-semibold text-gray-800">
          Expected dots (permanent-dots oracle)
        </span>
        <span className="text-xs text-gray-500">
          read-only — authored in test-scenarios/*.json, then re-seeded
        </span>
      </div>
      {dots == null ? (
        <p className="text-xs text-gray-400 italic">Not under test for this scenario.</p>
      ) : dots.length === 0 ? (
        <p className="text-xs text-gray-600 italic">
          Asserts <span className="font-mono">no dots at all</span>, on any side.
        </p>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Side</th>
                <th className="text-left px-3 py-2 font-medium">Field</th>
                <th className="text-left px-3 py-2 font-medium">Kind</th>
                <th className="text-left px-3 py-2 font-medium">Value contains</th>
                <th className="text-left px-3 py-2 font-medium">Previous contains</th>
                <th className="text-left px-3 py-2 font-medium">Why</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {dots.map((d, i) => (
                <tr key={i} className="align-top">
                  <td className="px-3 py-2 text-gray-800">{d.side}</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-800">{d.field}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${
                        d.kind === 'updated'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      {d.kind}
                    </span>
                    {d.optional && (
                      <span className="ml-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                        optional
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-700">
                    {d.valueContains || '—'}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-700">
                    {d.previousContains || '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">{d.why || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Editable form shapes (string-friendly for inputs) --------------------

interface FormSide {
  label: string;
  gender: string;
  interestedIn: string;
  tone: string;
  hasVenue: boolean;
  venueName: string;
  venueAddress: string;
  venueLat: string;
  venueLng: string;
  describes: string[];
}

interface FormExpected {
  a: string;
  b: string;
  match: boolean;
  // Read-only passenger, like expectedDots: authored in test-scenarios/*.json
  // and carried straight back on save. null = key absent (the run skill's
  // "matched" default applies), which must never be written out as a value.
  expectedTerminal: ExpectedTerminal | null;
  why: string;
}

/** The optional band-pair terminal contract, read-only. Blank when absent —
 *  absent means "no contract stated", which the run skill grades as `matched`
 *  without the scenario ever storing that word. */
function TerminalBadge({ value }: { value: ExpectedTerminal | null }) {
  if (!value) return <span className="text-xs text-gray-300">—</span>;
  const cls =
    value === 'abandoned'
      ? 'bg-rose-100 text-rose-700'
      : value === 'either'
        ? 'bg-sky-100 text-sky-700'
        : 'bg-emerald-100 text-emerald-700';
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded ${cls}`}
      title="expected_terminal — read-only, authored in test-scenarios/*.json"
    >
      {value}
    </span>
  );
}

interface FormState {
  name: string;
  storyType: string;
  situation: string;
  proximityM: string;
  hasTimeWindow: boolean;
  datetime: string;
  toleranceMin: string;
  guidance: string;
  notes: string;
  sides: FormSide[];
  expectedMatches: FormExpected[];
  // Read-only passenger: shown but not edited here, and written straight back
  // so an edit in this form can never wipe a scenario's dots oracle.
  expectedDots: ExpectedDot[] | null;
}

const emptySide = (label: string): FormSide => ({
  label,
  gender: 'female',
  interestedIn: 'male',
  tone: '',
  hasVenue: true,
  venueName: '',
  venueAddress: '',
  venueLat: '',
  venueLng: '',
  describes: [],
});

const EMPTY_FORM: FormState = {
  name: '',
  storyType: 'stranger',
  situation: '',
  proximityM: '',
  hasTimeWindow: true,
  datetime: '',
  toleranceMin: '60',
  guidance: '',
  notes: '',
  sides: [emptySide('A'), emptySide('B')],
  expectedMatches: [{ a: 'A', b: 'B', match: true, expectedTerminal: null, why: '' }],
  expectedDots: null,
};

function scenarioToForm(s: TestScenario): FormState {
  return {
    name: s.name,
    storyType: s.storyType,
    situation: s.situation ?? '',
    proximityM: s.proximityM != null ? String(s.proximityM) : '',
    hasTimeWindow: !!s.timeWindow,
    datetime: s.timeWindow?.datetime ?? '',
    toleranceMin:
      s.timeWindow?.toleranceMin != null ? String(s.timeWindow.toleranceMin) : '',
    guidance: s.guidance ?? '',
    notes: s.notes ?? '',
    sides: (s.sides ?? []).map((side) => ({
      label: side.label,
      gender: side.gender ?? '',
      interestedIn: side.interestedIn ?? '',
      tone: side.tone ?? '',
      hasVenue: !!side.venue,
      venueName: side.venue?.name ?? '',
      venueAddress: side.venue?.address ?? '',
      venueLat: side.venue?.lat != null ? String(side.venue.lat) : '',
      venueLng: side.venue?.lng != null ? String(side.venue.lng) : '',
      describes: side.describes ?? [],
    })),
    expectedMatches: (s.expectedMatches ?? []).map((m) => ({
      a: m.a,
      b: m.b,
      match: m.match,
      expectedTerminal: m.expectedTerminal ?? null,
      why: m.why ?? '',
    })),
    expectedDots: s.expectedDots ?? null,
  };
}

function formToBody(f: FormState): Partial<TestScenario> {
  const sides: ScenarioSide[] = f.sides.map((s) => ({
    label: s.label.trim(),
    gender: s.gender || null,
    interestedIn: s.interestedIn || null,
    tone: s.tone.trim(),
    venue: s.hasVenue
      ? {
          name: s.venueName.trim(),
          address: s.venueAddress.trim() || null,
          lat: s.venueLat.trim() ? parseFloat(s.venueLat) : null,
          lng: s.venueLng.trim() ? parseFloat(s.venueLng) : null,
        }
      : null,
    describes: s.describes,
  }));
  // expectedTerminal is only emitted when the scenario actually carries one —
  // an absent key must stay absent rather than be saved as its default.
  const expectedMatches: ExpectedMatch[] = f.expectedMatches
    .filter((m) => m.a && m.b)
    .map((m) => ({
      a: m.a,
      b: m.b,
      match: m.match,
      ...(m.expectedTerminal ? { expectedTerminal: m.expectedTerminal } : {}),
      why: m.why.trim() || null,
    }));
  return {
    name: f.name.trim(),
    storyType: f.storyType,
    situation: f.situation.trim(),
    proximityM: f.proximityM.trim() ? parseInt(f.proximityM, 10) : null,
    timeWindow: f.hasTimeWindow
      ? {
          datetime: f.datetime.trim(),
          toleranceMin: f.toleranceMin.trim() ? parseInt(f.toleranceMin, 10) : null,
        }
      : null,
    sides,
    expectedMatches,
    expectedDots: f.expectedDots,
    guidance: f.guidance.trim() || null,
    notes: f.notes.trim() || null,
  };
}

/** PD-5. The card's replay half: the launch commands, the runs recorded for
 *  this scenario, their parity reports and the legacy-vs-split comparison. It
 *  is collapsed by default and mounted only when opened — a page of twenty-eight
 *  scenarios must not fire twenty-eight run reads to draw a list. */
function RunsSection({ scenario }: { scenario: TestScenario }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 border-t pt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-sm text-pebbl-700 hover:underline"
        data-testid="runs-toggle"
      >
        {open ? '− Hide runs' : '+ Runs & replay'}
      </button>
      {open && (
        <div className="mt-3">
          <ScenarioRuns scenario={scenario} />
        </div>
      )}
    </div>
  );
}

export default function ScenariosPage() {
  const [scenarios, setScenarios] = useState<TestScenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null); // null = closed; '' = new
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  // When the editor opens (New or Edit), scroll it into view — otherwise editing
  // a scenario from the bottom of the list looks like nothing happened.
  useEffect(() => {
    if (editingId !== null) {
      editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [editingId]);

  const refresh = () =>
    listScenarios()
      .then((res) => setScenarios(res.scenarios))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));

  useEffect(() => {
    refresh();
  }, []);

  const startNew = () => {
    setForm(EMPTY_FORM);
    setEditingId('');
  };
  const startEdit = (s: TestScenario) => {
    setForm(scenarioToForm(s));
    setEditingId(s.id);
  };
  const cancel = () => setEditingId(null);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  // ---- sides -------------------------------------------------------------
  const labels = form.sides.map((s) => s.label).filter(Boolean);

  const setSide = (i: number, patch: Partial<FormSide>) =>
    setForm((prev) => ({
      ...prev,
      sides: prev.sides.map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
    }));

  const addSide = () =>
    setForm((prev) => {
      const nextLabel = String.fromCharCode(65 + prev.sides.length); // A, B, C…
      return { ...prev, sides: [...prev.sides, emptySide(nextLabel)] };
    });

  const removeSide = (i: number) =>
    setForm((prev) => ({ ...prev, sides: prev.sides.filter((_, idx) => idx !== i) }));

  const toggleDescribes = (i: number, label: string) =>
    setForm((prev) => ({
      ...prev,
      sides: prev.sides.map((s, idx) => {
        if (idx !== i) return s;
        const has = s.describes.includes(label);
        return {
          ...s,
          describes: has ? s.describes.filter((l) => l !== label) : [...s.describes, label],
        };
      }),
    }));

  // ---- expected matches --------------------------------------------------
  const setExpected = (i: number, patch: Partial<FormExpected>) =>
    setForm((prev) => ({
      ...prev,
      expectedMatches: prev.expectedMatches.map((m, idx) =>
        idx === i ? { ...m, ...patch } : m,
      ),
    }));
  const addExpected = () =>
    setForm((prev) => ({
      ...prev,
      expectedMatches: [
        ...prev.expectedMatches,
        { a: labels[0] ?? '', b: labels[1] ?? '', match: true, expectedTerminal: null, why: '' },
      ],
    }));
  const removeExpected = (i: number) =>
    setForm((prev) => ({
      ...prev,
      expectedMatches: prev.expectedMatches.filter((_, idx) => idx !== i),
    }));

  const save = async () => {
    if (!form.name.trim() || !form.situation.trim() || form.sides.length === 0) {
      alert('Name, situation and at least one side are required.');
      return;
    }
    if (form.sides.some((s) => !s.label.trim())) {
      alert('Every side needs a label.');
      return;
    }
    setSaving(true);
    try {
      const body = formToBody(form);
      if (editingId) await updateScenario(editingId, body);
      else await createScenario(body);
      setEditingId(null);
      await refresh();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to save scenario');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s: TestScenario) => {
    if (!confirm(`Delete scenario "${s.name}"?`)) return;
    try {
      await deleteScenario(s.id);
      setScenarios((prev) => prev.filter((x) => x.id !== s.id));
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading scenarios...</div>
      </div>
    );
  }

  const inputCls = 'mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Test Scenarios</h1>
          <p className="text-sm text-gray-500">
            A situation spec — the harness generates one story per side and runs
            them through the local chat + matcher.
          </p>
        </div>
        {editingId === null && (
          <button
            type="button"
            onClick={startNew}
            className="bg-pebbl-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-pebbl-700"
          >
            + New scenario
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error: {error}
        </div>
      )}

      {/* Editor */}
      {editingId !== null && (
        <div ref={editorRef} className="card space-y-5 border-2 border-pebbl-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {editingId ? 'Edit scenario' : 'New scenario'}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Name</span>
              <input
                className={inputCls}
                placeholder="two-guys-one-girl-disambiguation"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Type</span>
              <select className={inputCls} value={form.storyType} onChange={(e) => set('storyType', e.target.value)}>
                {STORY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Proximity (m between venues)</span>
              <input
                className={inputCls}
                placeholder="200"
                value={form.proximityM}
                onChange={(e) => set('proximityM', e.target.value)}
              />
            </label>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">
              Situation — neutral, omniscient description of what really happened
            </span>
            <textarea
              className={`${inputCls} font-mono`}
              rows={3}
              placeholder="Two guys and a girl are on Republic Square one afternoon. Both guys notice the girl; she only notices — and later describes — one of them."
              value={form.situation}
              onChange={(e) => set('situation', e.target.value)}
            />
          </label>

          {/* Time window */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={form.hasTimeWindow}
                onChange={(e) => set('hasTimeWindow', e.target.checked)}
              />
              Time window
            </label>
            {form.hasTimeWindow && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="datetime ISO (2026-06-20T15:00:00Z)"
                  value={form.datetime}
                  onChange={(e) => set('datetime', e.target.value)}
                />
                <input
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="tolerance minutes (60)"
                  value={form.toleranceMin}
                  onChange={(e) => set('toleranceMin', e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Sides */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-800">
                Sides ({form.sides.length}) — one person each
              </span>
              <button type="button" onClick={addSide} className="text-sm text-pebbl-700 hover:underline">
                + Add side
              </button>
            </div>
            {form.sides.map((s, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-3 bg-gray-50">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <label className="block">
                    <span className="text-xs text-gray-600">Label</span>
                    <input className={inputCls} value={s.label} onChange={(e) => setSide(i, { label: e.target.value })} />
                  </label>
                  <label className="block">
                    <span className="text-xs text-gray-600">Gender</span>
                    <select className={inputCls} value={s.gender} onChange={(e) => setSide(i, { gender: e.target.value })}>
                      {GENDERS.map((g) => (
                        <option key={g} value={g}>
                          {g || '(unset)'}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-xs text-gray-600">Interested in</span>
                    <select className={inputCls} value={s.interestedIn} onChange={(e) => setSide(i, { interestedIn: e.target.value })}>
                      {GENDERS.map((g) => (
                        <option key={g} value={g}>
                          {g || '(unset)'}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="flex items-end">
                    <button type="button" onClick={() => removeSide(i)} className="text-sm text-red-600 hover:underline pb-2">
                      Remove
                    </button>
                  </div>
                </div>
                <label className="block">
                  <span className="text-xs text-gray-600">Tone</span>
                  <input
                    className={inputCls}
                    placeholder="warm, hopeful; volunteers colour"
                    value={s.tone}
                    onChange={(e) => setSide(i, { tone: e.target.value })}
                  />
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input type="checkbox" checked={s.hasVenue} onChange={(e) => setSide(i, { hasVenue: e.target.checked })} />
                  Has venue pin
                </label>
                {s.hasVenue && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <input className="border border-gray-300 rounded-md px-3 py-2 text-sm md:col-span-2" placeholder="Venue name" value={s.venueName} onChange={(e) => setSide(i, { venueName: e.target.value })} />
                    <input className="border border-gray-300 rounded-md px-3 py-2 text-sm md:col-span-2" placeholder="Address" value={s.venueAddress} onChange={(e) => setSide(i, { venueAddress: e.target.value })} />
                    <input className="border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="lat" value={s.venueLat} onChange={(e) => setSide(i, { venueLat: e.target.value })} />
                    <input className="border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="lng" value={s.venueLng} onChange={(e) => setSide(i, { venueLng: e.target.value })} />
                  </div>
                )}
                <div>
                  <span className="text-xs text-gray-600">Describes (who this person noticed):</span>
                  <div className="flex flex-wrap gap-3 mt-1">
                    {labels.filter((l) => l !== s.label).length === 0 && (
                      <span className="text-xs text-gray-400 italic">add other sides first</span>
                    )}
                    {labels
                      .filter((l) => l !== s.label)
                      .map((l) => (
                        <label key={l} className="flex items-center gap-1 text-sm">
                          <input type="checkbox" checked={s.describes.includes(l)} onChange={() => toggleDescribes(i, l)} />
                          {l}
                        </label>
                      ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Expected matches */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-800">
                Expected matches (pairwise oracle)
                <span className="ml-2 text-xs font-normal text-gray-500">
                  terminal column is read-only — authored in test-scenarios/*.json
                </span>
              </span>
              <button type="button" onClick={addExpected} className="text-sm text-pebbl-700 hover:underline">
                + Add pair
              </button>
            </div>
            {form.expectedMatches.map((m, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                <select className="border border-gray-300 rounded-md px-2 py-2 text-sm md:col-span-2" value={m.a} onChange={(e) => setExpected(i, { a: e.target.value })}>
                  <option value="">a…</option>
                  {labels.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
                <select className="border border-gray-300 rounded-md px-2 py-2 text-sm md:col-span-2" value={m.b} onChange={(e) => setExpected(i, { b: e.target.value })}>
                  <option value="">b…</option>
                  {labels.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
                <label className="flex items-center gap-1 text-sm md:col-span-2">
                  <input type="checkbox" checked={m.match} onChange={(e) => setExpected(i, { match: e.target.checked })} />
                  should match
                </label>
                <div className="md:col-span-2 flex items-center">
                  <TerminalBadge value={m.expectedTerminal} />
                </div>
                <input className="border border-gray-300 rounded-md px-3 py-2 text-sm md:col-span-3" placeholder="why (optional)" value={m.why} onChange={(e) => setExpected(i, { why: e.target.value })} />
                <button type="button" onClick={() => removeExpected(i)} className="text-sm text-red-600 hover:underline md:col-span-1">
                  ✕
                </button>
              </div>
            ))}
          </div>

          {/* Expected dots — read-only */}
          <ExpectedDotsPanel dots={form.expectedDots} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Guidance (optional)</span>
              <textarea className={inputCls} rows={2} value={form.guidance} onChange={(e) => set('guidance', e.target.value)} />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Notes (optional)</span>
              <textarea className={inputCls} rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </label>
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={save} disabled={saving} className="bg-pebbl-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-pebbl-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save scenario'}
            </button>
            <button type="button" onClick={cancel} className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="grid gap-4">
        {scenarios.map((s) => (
          <div key={s.id} className="card relative">
            <div className="flex justify-between items-start gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${typeBadgeClass(s.storyType)}`}>
                    {s.storyType}
                  </span>
                  <span className="font-semibold text-gray-900">{s.name}</span>
                  <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                    {(s.sides ?? []).length} sides
                  </span>
                  {s.proximityM != null && (
                    <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">~{s.proximityM}m</span>
                  )}
                  {(s.expectedMatches ?? []).length > 0 && (
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
                      {s.expectedMatches.filter((m) => m.match).length}✓ /{' '}
                      {s.expectedMatches.filter((m) => !m.match).length}✗ pairs
                    </span>
                  )}
                  {(s.expectedMatches ?? []).some((m) => m.expectedTerminal) && (
                    <span className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded">
                      terminal:{' '}
                      {Array.from(
                        new Set(
                          (s.expectedMatches ?? [])
                            .map((m) => m.expectedTerminal)
                            .filter(Boolean) as string[],
                        ),
                      ).join(', ')}
                    </span>
                  )}
                  {s.expectedDots != null && (
                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                      {s.expectedDots.length === 0
                        ? 'dots: none expected'
                        : `${s.expectedDots.length} dot${s.expectedDots.length === 1 ? '' : 's'}`}
                    </span>
                  )}
                </div>
                <p className="text-gray-700 line-clamp-2">{s.situation}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {(s.sides ?? []).map((sd) => sd.label).join(', ')}
                </p>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button type="button" onClick={() => startEdit(s)} className="text-sm text-pebbl-700 hover:underline">
                  Edit
                </button>
                <button type="button" onClick={() => remove(s)} className="text-sm text-red-600 hover:underline">
                  Delete
                </button>
              </div>
            </div>
            <RunsSection scenario={s} />
          </div>
        ))}

        {scenarios.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No scenarios yet. Click “New scenario” to write one.
          </div>
        )}
      </div>
    </div>
  );
}
